import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Message,
  ChatRequest,
  ChatResponse,
  ProviderConfig,
  StreamEvent,
  ToolInfo,
} from "../types";
import {
  command,
  subscribe,
  startWebStream,
  IS_TAURI,
  isEmbedMode,
} from "./backend";

type UnlistenFn = () => void;

interface ChatContextType {
  messages: Message[];
  input: string;
  setInput: (value: string) => void;
  sendMessage: (content: string) => Promise<void>;
  isLoading: boolean;
  config: ProviderConfig | null;
  setConfig: (config: ProviderConfig) => Promise<void>;
  streaming: boolean;
  setStreaming: (v: boolean) => void;
  tools: ToolInfo[];
  clearChat: () => void;
  debug: boolean;
  setDebug: (v: boolean) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const SESSION_KEY = "ctsi-session-id";

// Third-party iframes may block localStorage access (throws SecurityError);
// fall back to an in-memory id so the widget still works, just without
// persistence across reloads.
const memoryStorage = new Map<string, string>();

function safeStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return memoryStorage.get(key) ?? null;
  }
}

function safeStorageSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    memoryStorage.set(key, value);
  }
}

function loadSessionId(): string {
  let id = safeStorageGet(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    safeStorageSet(SESSION_KEY, id);
  }
  return id;
}

function rotateSessionId(): string {
  const id = crypto.randomUUID();
  safeStorageSet(SESSION_KEY, id);
  return id;
}

const WELCOME_MESSAGE: Message = {
  id: "welcome",
  role: "assistant",
  content:
    "Hello! I'm your CTSI AI assistant. I can help you navigate the Clinical and Translational Science Institute resources at UB. How can I help you today?",
  timestamp: Date.now(),
};

export function ChatProvider({
  children,
  externalSessionId,
  isAdmin = true,
}: {
  children: React.ReactNode;
  /** Session id managed by the host page (embed mode) — stored in the HOST's
   *  first-party localStorage by embed.js so chats persist across pages in
   *  every browser. Rotation is delegated to the host via postMessage. */
  externalSessionId?: string;
  /** False for embed-role sessions, which can't read the admin-only config. */
  isAdmin?: boolean;
}) {
  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfigState] = useState<ProviderConfig | null>(null);
  const [streaming, setStreaming] = useState(true);
  const [tools, setTools] = useState<ToolInfo[]>([]);
  const [debug, setDebug] = useState(false);
  const sessionRef = useRef<string>(externalSessionId ?? loadSessionId());
  const unlistenRefs = useRef<UnlistenFn[]>([]);

  // Buffered streaming: accumulate chunks and flush on animation frames
  const streamBufferRef = useRef<{ content: string; reasoning: string }>({
    content: "",
    reasoning: "",
  });
  const rafRef = useRef<number | null>(null);
  const streamIdRef = useRef<string>("");

  const flushStreamBuffer = useCallback(() => {
    const buf = streamBufferRef.current;
    if (!buf.content && !buf.reasoning) return;

    const contentToAdd = buf.content;
    const reasoningToAdd = buf.reasoning;
    buf.content = "";
    buf.reasoning = "";

    const id = streamIdRef.current;
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        return {
          ...m,
          content: m.content + contentToAdd,
          reasoning: (m.reasoning ?? "") + reasoningToAdd || undefined,
        };
      }),
    );
  }, []);

  const scheduleFlush = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      flushStreamBuffer();
    });
  }, [flushStreamBuffer]);

  useEffect(() => {
    // /api/config is admin-only; embed tokens are forbidden from it.
    if (isAdmin && !isEmbedMode()) {
      command<ProviderConfig>("get_config")
        .then(setConfigState)
        .catch(console.error);
    }
    command<ToolInfo[]>("list_tools").then(setTools).catch(console.error);
    command<ChatResponse>("get_session", { session_id: sessionRef.current })
      .then((response) => {
        const restored: Message[] = response.messages.map((rm, i) => ({
          id: crypto.randomUUID(),
          role: rm.role as Message["role"],
          content: rm.content ?? "",
          reasoning: rm.reasoning ?? undefined,
          toolCalls: rm.tool_calls ?? undefined,
          toolCallId: rm.tool_call_id ?? undefined,
          toolName: rm.tool_name ?? undefined,
          timestamp: i,
        }));
        if (restored.length > 0) setMessages(restored);
      })
      .catch(console.error);
  }, []);

  const setConfig = async (newConfig: ProviderConfig) => {
    const result = await command<ProviderConfig>("set_config", {
      config: newConfig,
    });
    setConfigState(result);
  };

  const clearChat = useCallback(() => {
    if (externalSessionId) {
      // The host page owns the id (embed mode): ask it to rotate and reload
      // the iframe with the new session id.
      sessionRef.current = externalSessionId;
      try {
        window.parent.postMessage({ type: "ctsi-chat:new-session" }, "*");
        return;
      } catch {
        // fall through to local rotation
      }
    }
    sessionRef.current = rotateSessionId();
    setMessages([{ ...WELCOME_MESSAGE, timestamp: Date.now() }]);
    setInput("");
    setIsLoading(false);
    cleanupListeners();
  }, [externalSessionId]);

  const cleanupListeners = () => {
    unlistenRefs.current.forEach((fn) => fn());
    unlistenRefs.current = [];
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const sendMessage = useCallback(
    async (content: string) => {
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: "user",
        content,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      const request: ChatRequest = {
        session_id: sessionRef.current,
        message: content,
      };

      try {
        if (streaming) {
          const currentStreamId = crypto.randomUUID();
          streamIdRef.current = currentStreamId;
          streamBufferRef.current = { content: "", reasoning: "" };

          setMessages((prev) => [
            ...prev,
            {
              id: currentStreamId,
              role: "assistant",
              content: "",
              timestamp: Date.now(),
              isStreaming: true,
            },
          ]);

          const unStream = await subscribe("chat-stream", (event) => {
            const data = event.payload as string;
            if (data === "[DONE]") {
              // Flush any remaining buffered content before marking done
              if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
              }
              flushStreamBuffer();
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === streamIdRef.current
                    ? { ...m, isStreaming: false }
                    : m,
                ),
              );
              return;
            }

            try {
              const chunk: StreamEvent = JSON.parse(data);
              if (chunk.content || chunk.reasoning) {
                streamBufferRef.current.content += chunk.content ?? "";
                streamBufferRef.current.reasoning += chunk.reasoning ?? "";
                scheduleFlush();
              }
            } catch {
              // Skip unparseable chunks
            }
          });

          const unToolCall = await subscribe("chat-tool-call", (event) => {
            const raw = event.payload;
            const tc = typeof raw === "string" ? JSON.parse(raw) : raw;

            setMessages((prev) => [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: "assistant" as const,
                content: "",
                timestamp: Date.now(),
                toolCalls: [
                  {
                    id: tc.id,
                    type: "function",
                    function: { name: tc.name, arguments: tc.arguments },
                  },
                ],
              },
            ]);
          });

          const unToolResult = await subscribe("chat-tool-result", (event) => {
            const raw = event.payload;
            const tr = typeof raw === "string" ? JSON.parse(raw) : raw;

            const newId = crypto.randomUUID();
            streamIdRef.current = newId;
            streamBufferRef.current = { content: "", reasoning: "" };

            setMessages((prev) => [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: "tool" as const,
                content: tr.output,
                timestamp: Date.now(),
                toolCallId: tr.id,
                toolName: tr.name,
              },
              {
                id: newId,
                role: "assistant" as const,
                content: "",
                timestamp: Date.now(),
                isStreaming: true,
              },
            ]);
          });

          const unDone = await subscribe("chat-stream-done", () => {
            // Final flush
            if (rafRef.current !== null) {
              cancelAnimationFrame(rafRef.current);
              rafRef.current = null;
            }
            flushStreamBuffer();
            setMessages((prev) =>
              prev.filter((m) => !(m.isStreaming && !m.content.trim())),
            );
            setIsLoading(false);
            cleanupListeners();
          });

          unlistenRefs.current = [unStream, unToolCall, unToolResult, unDone];

          if (IS_TAURI) {
            await command("chat_stream", { request });
          } else {
            await startWebStream(request);
          }
        } else {
          const response = await command<ChatResponse>("chat", { request });
          const now = Date.now();

          const newMessages: Message[] = response.messages.map((rm, i) => ({
            id: crypto.randomUUID(),
            role: rm.role as Message["role"],
            content: rm.content ?? "",
            reasoning: rm.reasoning ?? undefined,
            toolCalls: rm.tool_calls ?? undefined,
            toolCallId: rm.tool_call_id ?? undefined,
            toolName: rm.tool_name ?? undefined,
            timestamp: now + i,
          }));

          setMessages((prev) => [...prev, ...newMessages]);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Chat error:", error);
        // Guardrail rejections (rate limits, budget, busy) carry copy meant
        // for the user verbatim; everything else is plumbing.
        const friendly =
          (error as Error & { friendly?: boolean })?.friendly === true;
        const errorMessage: Message = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: friendly
            ? String((error as Error).message)
            : `Error: ${error}. Check that your LLM backend is running and configured correctly.`,
          timestamp: Date.now(),
        };
        setMessages((prev) => {
          const filtered = prev.filter((m) => !m.isStreaming);
          return [...filtered, errorMessage];
        });
        setIsLoading(false);
        cleanupListeners();
      }
    },
    [streaming, flushStreamBuffer, scheduleFlush],
  );

  return (
    <ChatContext.Provider
      value={{
        messages,
        input,
        setInput,
        sendMessage,
        isLoading,
        config,
        setConfig,
        streaming,
        setStreaming,
        tools,
        clearChat,
        debug,
        setDebug,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}

export { ChatContext };
